import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { AuthModal } from '../components/AuthModal/AuthModal';
import { Step } from '../components/AuthModal/AuthModal.types';
import { useAuthAnalytics } from '../hooks/useAuthAnalytics';
import { AffiliateService } from '../services/AffiliateService/AffiliateService';
import { SembonusUserInfo } from './UserProvider';

type ProviderProps = {
  onLoggedIn?: (user: SembonusUserInfo | undefined) => void;
};

type ContextProps = {
  open: (origin: string, step?: Step) => void;
};

const defaultContext: ContextProps = {
  open: () => undefined,
};

export const AuthModalContext = createContext<ContextProps>(defaultContext);

export const useAuthModal = () => useContext(AuthModalContext);

export const AuthModalProvider: React.FC<PropsWithChildren<ProviderProps>> = ({
  onLoggedIn: onLoggedInCustomEvent,
  children,
}) => {
  const { onModalOpenedEvent, onSignedUpEvent, onLoggedInEvent } =
    useAuthAnalytics();

  const [step, setStep] = useState<Step | undefined>(undefined);

  const close = () => setStep(undefined);

  const open = (origin: string, initialStep: Step = 'log-in') => {
    setStep(initialStep);
    onModalOpenedEvent(origin);
  };

  const onLoggedIn = (user: SembonusUserInfo | undefined) => {
    close();

    const travelAgentId = user?.member.travelAgencyId;

    if (travelAgentId) {
      AffiliateService.setAffiliateId(travelAgentId);
    }

    onLoggedInEvent(!!travelAgentId);
    onLoggedInCustomEvent?.(user);
  };

  const onSignedUp = ({
    optedInToSembonus,
  }: {
    optedInToSembonus: boolean;
  }) => {
    close();
    onSignedUpEvent({ optedInToSembonus });
  };

  return (
    <AuthModalContext.Provider value={{ open }}>
      {children}

      <AuthModal
        step={step}
        setStep={setStep}
        close={close}
        onSignedUp={onSignedUp}
        onLoggedIn={onLoggedIn}
      />
    </AuthModalContext.Provider>
  );
};
